import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { tierStyle } from "../lib/tiers";

/**
 * Encart affiché à la place d'un outil quand l'offre du lecteur ne le couvre pas.
 * Accès strict par offre : un outil Pro n'est pas inclus dans Essentiel.
 * - tier        : offre requise par l'outil ("essentiel" | "pro")
 * - currentTier : offre actuelle du lecteur ("free" | "essentiel" | ...)
 * - loggedIn    : false → lien « Déjà abonné ? Se connecter »
 */
export default function ToolPaywall({ tier, currentTier = "free", loggedIn = false }) {
  const { data: cfg } = useQuery({
    queryKey: ["config"],
    queryFn: async () => (await api.get("/config")).data,
  });
  const entry = cfg?.tiers?.[tier];
  const name = entry?.name || (tier === "pro" ? "Pro" : "Essentiel");
  const price = entry ? `${entry.price_eur.toString().replace(".", ",")} €/mois` : null;
  const currentName = cfg?.tiers?.[currentTier]?.name || currentTier;
  const isUpgrade = currentTier && currentTier !== "free";
  const s = tierStyle(tier); // couleur de l'offre requise

  return (
    <div
      className={`relative rounded-xl border bg-gradient-to-br to-transparent p-6 sm:p-8 md:p-10 text-center overflow-hidden ${s.panel}`}
      data-testid="tool-paywall"
    >
      <div className="absolute inset-0 pointer-events-none opacity-40 grid-noise" />

      <div className={`relative inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[11px] uppercase tracking-[0.2em] ${s.badge}`}>
        <Lock className="w-3 h-3" /> Outil {name}
      </div>

      <h3 className="relative mt-5 font-display font-black text-2xl md:text-3xl">
        Cet outil est inclus dès l'offre {name}
      </h3>
      <p className="relative mt-3 text-zinc-400 max-w-xl mx-auto">
        {isUpgrade
          ? `Votre abonnement ${currentName} ne comprend pas cet outil. Passez à ${name} pour l'utiliser, ainsi que tous les simulateurs avancés et les portefeuilles Side B suivis en direct.`
          : `Abonnez-vous à l'offre ${name} pour utiliser cet outil, ainsi que tous les autres outils et contenus inclus dans cette offre.`}
      </p>

      <div className="relative mt-6 flex flex-col items-center gap-4">
        {/* Bouton à la couleur de l'offre requise (orange Essentiel, vert Pro, violet Max) ;
            téléphone : le prix passe sous le bouton pour que le texte tienne sur une ligne */}
        <Button
          asChild
          size="lg"
          className={`h-11 ${s.cta}`}
          data-testid="tool-paywall-cta"
        >
          <Link to="/upgrade">
            {isUpgrade ? `Passer à ${name}` : `Débloquer avec ${name}`}
            {price ? <span className="hidden sm:inline">{` · ${price}`}</span> : ""}
            <ArrowRight className="ml-1 w-4 h-4" />
          </Link>
        </Button>
        {price && <div className="sm:hidden -mt-2 text-sm text-zinc-300">{price}</div>}
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm text-zinc-500">
          <Link to="/upgrade" className="hover:text-zinc-200 underline-offset-4 hover:underline">
            Voir toutes les offres
          </Link>
          {!loggedIn && (
            <>
              <span>·</span>
              <Link to="/login" className="hover:text-zinc-200 underline-offset-4 hover:underline">
                Déjà abonné ? Se connecter
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
